import { useState } from 'react';
import { DropdownMenu, toast, type DropdownMenuItem } from '@hrms/ui';
import { Building2 } from 'lucide-react';
import { tsr } from '../../lib/api.js';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { LazyCreateCompanyDialog } from '../company-group/LazyCreateCompanyDialog.js';
import { goToCompany, useCompanyGroup } from '../company-group/useCompanyGroup.js';

/**
 * "Switch company" for a person who holds accounts in more than one organisation — NOT the platform
 * owner's own switcher (`AppShell.tsx`'s `impersonatedByPlatformAdmin` button), which enters an org
 * she has no account in at all. This one lists the organisations linked to her account (see
 * `CrossOrgIdentityLink`: by a platform admin, by creating a company from inside, or by Group
 * access — docs/adr/0034-company-groups.md), and ends with "New company" for someone who may create
 * one. It renders nothing for the overwhelming majority of people, who have neither.
 *
 * The switch itself lands on the other company's own subdomain with a one-time code
 * (`goToCompany`), because a session lives in that origin's storage.
 */
export function OrgSwitcher() {
  const { me } = useAuthenticatedSession();
  // `/auth/me` says whether there is anything to list, so the many people with no linked company
  // never ask, and nobody asks again on every page. An older API that does not send the flag yet
  // (a rolling deploy) leaves it undefined, which still asks, as before.
  const linked = tsr.auth.listLinkedOrganizations.useQuery({
    queryKey: ['auth', 'linked-organizations'],
    enabled: me.hasLinkedOrganizations !== false,
    staleTime: 10 * 60_000,
  });
  const switchTo = tsr.auth.switchToLinkedOrganization.useMutation();
  const { canCreateCompany } = useCompanyGroup();
  const [creating, setCreating] = useState(false);

  const organizations = linked.data?.status === 200 ? linked.data.body.organizations : [];
  if (organizations.length === 0 && !canCreateCompany) return null;

  const items: DropdownMenuItem[] = organizations.map((org) => ({
    label: org.name,
    onSelect: () => {
      switchTo.mutate(
        { params: { orgId: org.id } },
        {
          onSuccess: ({ body }) => goToCompany(body.organization.slug, body.code),
          // Access to that company was taken away since this menu loaded.
          onError: () => {
            toast.error(`You no longer have access to ${org.name}.`);
            void linked.refetch();
          },
        },
      );
    },
  }));
  if (canCreateCompany) items.push({ label: '+ New company', onSelect: () => setCreating(true) });

  return (
    <>
      <DropdownMenu
        trigger={
          <span
            role="button"
            tabIndex={0}
            aria-label={organizations.length > 0 ? 'Switch company' : 'Companies'}
            className="flex min-h-10 min-w-10 items-center justify-center gap-1.5 rounded-full border border-border bg-bg-subtle px-2.5 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:border-border-strong hover:text-fg sm:min-h-0 sm:min-w-0 sm:px-3"
          >
            <Building2 className="size-4 sm:size-3.5" />
            {/* Icon only on a phone, where the top bar has no room for words. */}
            <span className="hidden sm:inline">
              {organizations.length > 0 ? 'Switch company' : 'Companies'}
            </span>
          </span>
        }
        items={items}
      />
      <LazyCreateCompanyDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}
